
      const STORAGE_KEY = 'webxr-feature-combinator-v1';
      const LOG_PREFIX = '[WXR-ISO]';

      // EARLY: apply Chromium projection-layer workaround if previously
      // enabled. Must run before three.js touches any WebXR API. Reading
      // sessionStorage and deleting the prototype members is synchronous,
      // so doing it before the dynamic imports below is sufficient.
      let workaroundApplied = { createProjectionLayer: false, layers: false };
      try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        const state = raw ? JSON.parse(raw) : null;
        if (state?.workaround) {
          if (
            typeof XRWebGLBinding !== 'undefined' &&
            'createProjectionLayer' in XRWebGLBinding.prototype
          ) {
            delete XRWebGLBinding.prototype.createProjectionLayer;
            workaroundApplied.createProjectionLayer = true;
          }
          if (
            typeof XRRenderState !== 'undefined' &&
            'layers' in XRRenderState.prototype
          ) {
            delete XRRenderState.prototype.layers;
            workaroundApplied.layers = true;
          }
          console.log(
            `${LOG_PREFIX} applied projection-layer workaround:`,
            workaroundApplied
          );
        }
      } catch {
        /* ignore — workaround is best-effort */
      }

      const THREE = await import('three');
      const { ARButton } = await import('three/addons/webxr/ARButton.js');

      const logEl = document.getElementById('log');
      const payloadEl = document.getElementById('payload-preview');
      const slot = document.getElementById('ar-button-slot');
      const startBtn = document.getElementById('start-ar');

      const requiredInputs = document.querySelectorAll(
        'input[name="required"]'
      );
      const optCameraAccess = document.getElementById('opt-camera-access');
      const optDomOverlay = document.getElementById('opt-dom-overlay');
      const optDepthSensing = document.getElementById('opt-depth-sensing');
      const optWorkaround = document.getElementById(
        'opt-projection-layer-workaround'
      );
      const workaroundStatus = document.getElementById('workaround-status');

      function log(msg, cls) {
        const stamp = new Date().toISOString().slice(11, 23);
        const line = document.createElement('div');
        if (cls) line.className = cls;
        line.textContent = `${stamp} ${msg}`;
        logEl.appendChild(line);
        logEl.scrollTop = logEl.scrollHeight;
        // eslint-disable-next-line no-console -- diagnostic page
        console.log(`${LOG_PREFIX} ${msg}`);
      }

      function getRequired() {
        for (const input of requiredInputs) {
          if (input.checked) return input.value;
        }
        return 'hit-test';
      }

      function buildSessionInit() {
        const required = getRequired();
        const optional = [];
        if (optCameraAccess.checked) optional.push('camera-access');
        if (optDomOverlay.checked) optional.push('dom-overlay');
        if (optDepthSensing.checked) optional.push('depth-sensing');
        const init = { requiredFeatures: [required] };
        if (optional.length) init.optionalFeatures = optional;
        if (optDomOverlay.checked) {
          init.domOverlay = { root: document.body };
        }
        if (optDepthSensing.checked) {
          init.depthSensing = {
            usagePreference: ['cpu-optimized'],
            dataFormatPreference: ['luminance-alpha', 'float32'],
          };
        }
        return init;
      }

      function serializablePayload(init) {
        // Replace the DOM root with a placeholder string so JSON.stringify works.
        const copy = { ...init };
        if (copy.domOverlay) copy.domOverlay = { root: '<body>' };
        return copy;
      }

      function updatePayloadPreview() {
        const init = buildSessionInit();
        payloadEl.textContent = JSON.stringify(
          serializablePayload(init),
          null,
          2
        );
      }

      function saveState() {
        const state = {
          required: getRequired(),
          cameraAccess: optCameraAccess.checked,
          domOverlay: optDomOverlay.checked,
          depthSensing: optDepthSensing.checked,
          workaround: optWorkaround.checked,
        };
        try {
          sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
        } catch {
          /* ignore */
        }
      }

      function loadState() {
        try {
          const raw = sessionStorage.getItem(STORAGE_KEY);
          if (!raw) return;
          const state = JSON.parse(raw);
          for (const input of requiredInputs) {
            input.checked = input.value === state.required;
          }
          optCameraAccess.checked = !!state.cameraAccess;
          optDomOverlay.checked = !!state.domOverlay;
          optDepthSensing.checked = !!state.depthSensing;
          optWorkaround.checked = !!state.workaround;
        } catch {
          /* ignore */
        }
      }

      for (const input of [
        ...requiredInputs,
        optCameraAccess,
        optDomOverlay,
        optDepthSensing,
      ]) {
        input.addEventListener('change', () => {
          saveState();
          updatePayloadPreview();
        });
      }

      // Workaround toggle: save state then reload so the early `delete`
      // block at the top of the script can run with the new value before
      // any WebXR API is touched.
      optWorkaround.addEventListener('change', () => {
        saveState();
        workaroundStatus.textContent = ' — reloading…';
        setTimeout(() => location.reload(), 50);
      });

      loadState();
      updatePayloadPreview();
      if (optWorkaround.checked) {
        workaroundStatus.textContent =
          workaroundApplied.createProjectionLayer ||
          workaroundApplied.layers
            ? ' — applied this load'
            : ' — enabled but globals absent on this device';
      }

      // Three.js scene setup — intentionally identical in shape to the upstream
      // webxr_ar_hittest.html example.
      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(
        70,
        window.innerWidth / window.innerHeight,
        0.01,
        20
      );

      const light = new THREE.HemisphereLight(0xffffff, 0xbbbbff, 3);
      light.position.set(0.5, 1, 0.25);
      scene.add(light);

      const renderer = new THREE.WebGLRenderer({
        antialias: true,
        alpha: true,
      });
      renderer.setPixelRatio(window.devicePixelRatio);
      renderer.setSize(window.innerWidth, window.innerHeight);
      renderer.xr.enabled = true;
      renderer.domElement.style.position = 'fixed';
      renderer.domElement.style.inset = '0';
      renderer.domElement.style.zIndex = '-1';
      document.body.appendChild(renderer.domElement);

      const reticle = new THREE.Mesh(
        new THREE.RingGeometry(0.15, 0.2, 32).rotateX(-Math.PI / 2),
        new THREE.MeshBasicMaterial()
      );
      reticle.matrixAutoUpdate = false;
      reticle.visible = false;
      scene.add(reticle);

      let hitTestSource = null;
      let hitTestSourceRequested = false;
      let frameCount = 0;
      let lastFrameLogTime = 0;

      function animate(_timestamp, frame) {
        if (frame) {
          frameCount++;
          const now = performance.now();
          if (now - lastFrameLogTime > 1000) {
            log(`frame #${frameCount}`, 'info');
            lastFrameLogTime = now;
          }

          const referenceSpace = renderer.xr.getReferenceSpace();
          const session = renderer.xr.getSession();

          const required = getRequired();
          if (required === 'hit-test' && !hitTestSourceRequested) {
            session.requestReferenceSpace('viewer').then((viewerSpace) => {
              session
                .requestHitTestSource({ space: viewerSpace })
                .then((source) => {
                  hitTestSource = source;
                  log('hit-test source acquired', 'ok');
                })
                .catch((err) => log(`hit-test source error: ${err}`, 'err'));
            });
            session.addEventListener('end', () => {
              hitTestSourceRequested = false;
              hitTestSource = null;
            });
            hitTestSourceRequested = true;
          }

          if (hitTestSource && referenceSpace) {
            const results = frame.getHitTestResults(hitTestSource);
            if (results.length) {
              const hit = results[0];
              reticle.visible = true;
              reticle.matrix.fromArray(
                hit.getPose(referenceSpace).transform.matrix
              );
            } else {
              reticle.visible = false;
            }
          }
        }
        renderer.render(scene, camera);
      }
      renderer.setAnimationLoop(animate);

      window.addEventListener('resize', () => {
        camera.aspect = window.innerWidth / window.innerHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(window.innerWidth, window.innerHeight);
      });

      window.addEventListener('error', (event) => {
        log(`window error: ${event.message}`, 'err');
      });
      window.addEventListener('unhandledrejection', (event) => {
        log(`unhandled rejection: ${event.reason}`, 'err');
      });

      function rebuildArButton() {
        slot.replaceChildren();
        frameCount = 0;
        hitTestSource = null;
        hitTestSourceRequested = false;
        const init = buildSessionInit();
        log(
          `creating ARButton with ${JSON.stringify(serializablePayload(init))}`,
          'info'
        );
        const btn = ARButton.createButton(renderer, init);
        btn.addEventListener('click', () => log('ARButton clicked', 'info'));
        slot.appendChild(btn);
      }

      renderer.xr.addEventListener('sessionstart', () => {
        log('sessionstart', 'ok');
      });
      renderer.xr.addEventListener('sessionend', () => {
        log(`sessionend (total frames: ${frameCount})`, 'info');
      });

      startBtn.addEventListener('click', rebuildArButton);

      log(`ready. Three.js r${THREE.REVISION}`, 'ok');
      if (
        workaroundApplied.createProjectionLayer ||
        workaroundApplied.layers
      ) {
        log(
          `Chromium projection-layer workaround active (deleted: ${[
            workaroundApplied.createProjectionLayer && 'createProjectionLayer',
            workaroundApplied.layers && 'XRRenderState.layers',
          ]
            .filter(Boolean)
            .join(', ')})`,
          'info'
        );
      } else if (optWorkaround.checked) {
        log(
          'Workaround toggle is on, but neither prototype member existed to delete.',
          'info'
        );
      }
      if (!navigator.xr) {
        log('navigator.xr is undefined on this device/browser', 'err');
      } else {
        navigator.xr
          .isSessionSupported('immersive-ar')
          .then((supported) =>
            log(
              `immersive-ar supported: ${supported}`,
              supported ? 'ok' : 'err'
            )
          )
          .catch((err) => log(`isSessionSupported error: ${err}`, 'err'));
      }
    